<template>
  <div class="sample-survey-main h-full edit">
    <update-notify />
    <div class="mb-6">
      <title-and-welcome />
    </div>
    <div class="question-group" ref="questionList">
      <draggable animation="600" :value="questionList" @end="onMoveEnd" handle=".question-move-class">
        <transition-group tag="div" class="edit-question-wrapper">
          <sample-question
            v-for="(question, index) in questionList"
            :key="question.q_id"
            :is-last="index === questionList.length - 1"
            :question="question"
            @remove="onRemove"
          />
        </transition-group>
      </draggable>
    </div>
    <div class="survey-main-bottom"></div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Draggable from 'vuedraggable'

import { SET_QUESTION_LIST, SET_SELECT_QUESTION } from '@/store/survey/mutation-types'
import {
  ADD_QUESTION,
  CHANGE_WINDOW_CLICK_SELECT_QUESTION,
  MOVE_QUESTION_LIST,
  REMOVE_QUESTION_LIST,
  TO_QUESTION_POS
} from '@/assets/js/config/bus'
import { isPage } from '@/assets/js/question-util'
import GlobalConfig from '@/global/config'
import { createQuestion, normalizeNum } from '@/question/factory'
import { Queue } from '@dygc/shared'
import { surveyLoading } from '@/assets/js/el-loading'
import { add } from '@/api/question'
import TitleAndWelcome from '@/views/survey/edit/TitleAndWelcome'
import UpdateNotify from '@/views/other-pages/sample-company/components/UpdateNotify'
import SampleQuestion from '@/views/other-pages/sample-company/components/SampleQuestion'

const queue = new Queue()
const cacheList = []
export default {
  name: 'SampleSurveyMain',
  components: { SampleQuestion, UpdateNotify, TitleAndWelcome, Draggable },
  computed: {
    ...mapGetters(['questionList'])
  },
  methods: {
    onRemove(question) {
      return this.removeList([question])
    },
    onMoveEnd(v) {
      this.onMove(v)
    },
    onMove(v, cb) {
      let { newIndex, oldIndex } = v
      if (newIndex === oldIndex) return
      if (newIndex < 0) newIndex = 0
      let list = this.questionList.slice()
      let element = list[oldIndex]
      list.splice(oldIndex, 1)
      list.splice(newIndex, 0, element)

      this.moveQuestionList({
        surveyId: GlobalConfig.getSurveyId(),
        preQuestionId: list[newIndex - 1]?.id || null,
        questionIds: [element.id],
        list
      }).then(() => {
        this.$surveyBus.$emit('onUpdateStatus', true)
        if (typeof cb === 'function') {
          cb()
        }
      })
    },
    onMoveList(v, cb) {
      let { newIndex, oldIndexList = [] } = v
      const questionList = this.questionList.slice()

      const appendList = []
      for (const index of oldIndexList) {
        appendList.push(questionList[index])
        questionList[index] = null
      }
      const preQuestionId = this.questionList[newIndex]?.id || null
      questionList.splice(newIndex + 1, 0, ...appendList)

      const finalList = questionList.filter(q => q !== null)
      if (isPage(finalList[0])) {
        this.$message.warning('第一题不能是分页')
        cb && cb(false)
        return
      }
      const list = this.questionList.slice()
      let isNeedMove = false
      for (let i = 0; i < list.length; i++) {
        if (list[i].id !== finalList[i].id) {
          isNeedMove = true
        }
      }

      if (!isNeedMove) {
        cb && cb(true)
        return
      }

      this.moveQuestionList({
        surveyId: GlobalConfig.getSurveyId(),
        preQuestionId: preQuestionId,
        questionIds: appendList.map(q => q.id),
        list: finalList
      }).then(() => {
        this.$surveyBus.$emit('onUpdateStatus', true)
        if (typeof cb === 'function') {
          cb(true)
        }
      })
    },
    removeList(questionList = [], cb) {
      // 没有逻辑 配额 随机题目 直接删除就行
      return this.removeQuestionAdapter(questionList.map(item => item.id)).then(() => {
        cb && cb(true)
        this.$surveyBus.$emit('onUpdateStatus')
      })
    },
    addQuestion(type) {
      createQuestion(type).then(question => {
        if (queue.getIsRunning()) {
          if (!cacheList.length) {
            queue.use(() => {
              let list = cacheList.slice()
              cacheList.length = 0
              let res = this.addList(list, null)
              this.$store.commit(SET_SELECT_QUESTION, list[list.length - 1])
              return res
            })
          }
          cacheList.push(question)
        } else {
          queue.use(() => {
            this.$surveyBus.$emit(TO_QUESTION_POS, question)
            let res = this.addList([question], null)
            this.$store.commit(SET_SELECT_QUESTION, question)
            return res
          })
        }
        queue.run()
      })
    },
    addList(list) {
      let preQuestion = this.$store.getters.selectQuestion
      if (!preQuestion) {
        preQuestion = this.questionList[this.questionList.length - 1]
      }
      let insertIdx = this.questionList.findIndex(q => q.q_id === preQuestion.q_id)
      const questionList = this.questionList.slice()
      questionList.splice(insertIdx + 1, 0, ...list)
      normalizeNum(questionList)
      this.$store.commit(SET_QUESTION_LIST, questionList)
      return new Promise((resolve, reject) => {
        surveyLoading(() => {
          return add({
            id: GlobalConfig.getSurveyId(),
            questions: list.map(q => q.getParams()),
            preQuestionId: preQuestion.id
          }).then(res => {
            let ids = res.data.data
            let id
            list.forEach((q, idx) => {
              id = q.id = ids[idx]
            })
            resolve(id)
          }, reject)
        })
      })
    },
    removeQuestionAdapter(ids = []) {
      let finalIds = ids
      let list = this.questionList.slice()
      let finalList = list.filter(q => !ids.includes(q.id))
      let tmp = finalList.shift()
      while (isPage(tmp)) {
        finalIds.push(tmp.id)
        tmp = finalList.shift()
      }
      let pageList = finalList.filter(isPage)
      let last = finalList.pop()
      if (pageList.length === 1 && isPage(last)) {
        finalIds.push(last.id)
      }
      return this.removeQuestionByIds({
        surveyId: GlobalConfig.getSurveyId(),
        ids: finalIds,
        group: false
      })
    },
    initBusHandler() {
      const clickWindowHandler = () => {
        this.$store.commit(SET_SELECT_QUESTION, null)
      }
      this.$surveyBus.$on(CHANGE_WINDOW_CLICK_SELECT_QUESTION, flag => {
        if (flag) {
          window.addEventListener('click', clickWindowHandler)
        } else {
          window.removeEventListener('click', clickWindowHandler)
        }
      })
      window.addEventListener('click', clickWindowHandler)

      this.$surveyBus.$on(REMOVE_QUESTION_LIST, this.removeList)
      this.$surveyBus.$on(MOVE_QUESTION_LIST, this.onMoveList)
      this.$surveyBus.$on(ADD_QUESTION, this.addQuestion)

      return function () {
        this.$surveyBus.$off(CHANGE_WINDOW_CLICK_SELECT_QUESTION)
        this.$surveyBus.$off(REMOVE_QUESTION_LIST)
        this.$surveyBus.$off(MOVE_QUESTION_LIST)
        this.$surveyBus.$off(ADD_QUESTION)
        window.removeEventListener('click', clickWindowHandler)
      }
    },
    ...mapActions(['removeQuestionByIds', 'moveQuestionList'])
  }
}
</script>

<style scoped lang="scss">
.sample-survey-main {
  padding: 16px 16px 0;
  position: relative;

  .survey-main-bottom {
    height: 20px;
  }
}
</style>
